import {
  ZERO, add, clampPos, distribute, formatMoney, minM, share, sub, type Money,
} from './money.js';
import { addMonths, diffDays, type PlainDate } from './date.js';
import { creditState } from './credit.js';
import { annualCost, savedPerYear } from './loans.js';
import { BUFFER_MONTHS, bufferStage, type BufferStage } from './plan.js';
import type { Account, CreditTerms, Goal, Loan } from './types.js';

export interface AllocationInput {
  /** Сколько распределяем — обычно свободная часть поступившего дохода. */
  amount: Money;
  goals: Goal[];
  accounts: Account[];
  creditTerms: CreditTerms[];
  /** Месячные расходы на повседневные нужды: от них считаются пороги подушки. */
  monthlyNeed: Money;
  today: PlainDate;
  /**
   * Обязательные минимумы по долгам уже покрыты внешним шагом.
   *
   * По умолчанию `true` — прежнее поведение для существующих вызовов.
   * `distributeIncome` передаёт `false`: иначе минимум по карте выделяется
   * дважды — сначала защитой обязательных платежей, потом шагом 1 водопада.
   */
  includeDebtMinimums?: boolean;
  /**
   * Сколько уже направлено цели вне водопада.
   *
   * Уменьшает её потолок: суммарно по обоим шагам цель не может получить
   * больше своего разрыва. Без этого выбор человека «внести в долг» удваивал
   * бы сумму.
   */
  alreadyAllocated?: readonly { goalId: string; amount: Money }[];
  /**
   * Долги. Цель `debt_payoff` ссылается на кредит через `loanId`, и порядок
   * досрочного гашения считается по ставке каждого из них. Без этого списка
   * поведение прежнее: минимум ищется по первой кредитной карте.
   */
  loans?: Loan[];
}

export interface Allocation {
  goalId: string;
  goalName: string;
  amount: Money;
  /** Почему именно столько — это показывается пользователю дословно. */
  reason: string;
  /** Место в очереди удовлетворения. */
  step: number;
}

export interface AllocationResult {
  allocations: Allocation[];
  /** Не расписанный остаток — он остаётся свободным, а не «исчезает». */
  leftover: Money;
}

/**
 * Приоритизация целей (PRD §2.3).
 *
 * Цели конкурируют за одни деньги, и порядок здесь — не предпочтение, а
 * арифметика: гасить кредитку под 30% раньше, чем копить на шкаф, выгоднее
 * при любых обстоятельствах. Движок обязан не просто выдать числа, но и
 * объяснить каждое — иначе пользователь воспринимает распределение как
 * произвол и переопределяет всё вручную.
 */
export function allocateIncome(input: AllocationInput): AllocationResult {
  const active = input.goals.filter((g) => g.status === 'active');
  const allocations: Allocation[] = [];
  let rest = input.amount;

  // Одна цель может получить деньги на нескольких шагах (минимум по долгу,
  // потом досрочное гашение). Потолок считается от **остатка с учётом уже
  // выданного в этом же проходе**, иначе цель переполняется.
  const given = new Map<string, Money>(
    (input.alreadyAllocated ?? []).map((a) => [a.goalId, a.amount]),
  );

  // Объяснение считается от суммы, которая реально досталась цели: обещать
  // экономию с полного остатка, вложив десятую часть, было бы неправдой.
  const take = (
    want: Money, goal: Goal, reason: string | ((amount: Money) => string), step: number,
  ): void => {
    if (rest <= ZERO || want <= ZERO) return;
    const already = given.get(goal.id) ?? ZERO;
    const room = clampPos(sub(goalGap(goal), already));
    if (room <= ZERO) return;
    const amount = minM(minM(want, rest), room);
    if (amount <= ZERO) return;
    allocations.push({
      goalId: goal.id, goalName: goal.name, amount, step,
      reason: typeof reason === 'function' ? reason(amount) : reason,
    });
    given.set(goal.id, add(already, amount));
    rest = sub(rest, amount);
  };

  const termsById = new Map(input.creditTerms.map((t) => [t.accountId, t]));
  const buffer = active.find((g) => g.kind === 'emergency_fund');

  // 1. Обязательный минимум по долгам — чтобы не улететь в проценты.
  //
  //    Минимум просит только карта: у ипотеки, автокредита и рассрочки платёж
  //    уже стоит в обязательствах, и брать его здесь второй раз значило бы
  //    вычесть одни деньги дважды. Цель по такому долгу — чистое досрочное
  //    гашение, и её место на шаге 4.
  const loansById = new Map((input.loans ?? []).map((l) => [l.id, l]));
  const loanOf = (g: Goal): Loan | undefined => (g.loanId ? loansById.get(g.loanId) : undefined);

  for (const g of input.includeDebtMinimums === false ? [] : active.filter((g) => g.kind === 'debt_payoff')) {
    const loan = loanOf(g);
    if (loan && loan.kind !== 'credit_card') continue;
    const cardAccountId = loan?.card?.accountId;
    const acc = cardAccountId
      ? input.accounts.find((a) => a.id === cardAccountId && termsById.has(a.id))
      // Цель без ссылки на кредит — прежнее поведение: первая кредитная карта.
      : input.accounts.find((a) => a.kind === 'credit_card' && termsById.has(a.id));
    if (!acc) continue;
    const st = creditState(acc, termsById.get(acc.id)!, input.today);
    // Неизвестный минимум ничего не требует: очередь распределения не должна
    // придумывать сумму, которой человек не называл.
    take(st.minPayment ?? ZERO, g, `Обязательный минимум по долгу — без него начнутся проценты`, 1);
  }

  // 2. Резервы под уже известные будущие платежи.
  for (const g of active.filter((g) => g.kind === 'sinking_fund')) {
    take(g.monthlyContribution, g, 'Резерв под будущий платёж — он придёт независимо от планов', 2);
  }

  // 3. Подушка до одного месяца расходов. Это идёт впереди досрочного гашения
  //    даже дорогого долга: без месячного запаса любая неожиданность снова
  //    отправляет человека в кредитку, и гашение обнуляется.
  if (buffer) {
    const stage = bufferStage(buffer.currentAmount, input.monthlyNeed);
    if (stage === 'critical') {
      const need = sub(share(input.monthlyNeed, BUFFER_MONTHS.critical), buffer.currentAmount);
      take(clampPos(need), buffer, 'Один месяц расходов — разница между неприятностью и катастрофой', 3);
    }
  }

  // 4. Досрочное гашение: по ставке, по убыванию.
  //
  //    Порядок считается, а не назначается: у кого-то дорогой долг — карта, у
  //    кого-то автокредит, а рассрочка под ноль сама оказывается последней.
  //    Долг, который нельзя гасить досрочно, денег не просит вовсе: предлагать
  //    невозможное хуже, чем молчать.
  const debtGoals = active
    .filter((g) => g.kind === 'debt_payoff')
    .filter((g) => loanOf(g)?.allowsEarly !== false)
    .sort((a, b) => (loanOf(b)?.ratePct ?? 0) - (loanOf(a)?.ratePct ?? 0));
  for (const g of debtGoals) {
    const loan = loanOf(g);
    take(goalGap(g), g, (amount) => earlyRepayReason(loan, amount), 4);
  }

  // 5. Подушка до трёх месяцев — базовая устойчивость.
  if (buffer) {
    const stage = bufferStage(buffer.currentAmount, input.monthlyNeed);
    if (stage === 'critical' || stage === 'base') {
      const need = sub(share(input.monthlyNeed, BUFFER_MONTHS.base), buffer.currentAmount);
      take(clampPos(need), buffer, 'База: три месяца расходов', 5);
    }
  }

  // 6. Цели высокой важности — сначала самые важные, потом по близости срока.
  //
  //    `critical` сюда входит наравне с `high`. Раньше шаг брал только `high`,
  //    а `critical` не попадал ни сюда, ни в шаг 8 — и самая важная для
  //    человека цель оставалась без денег вовсе. Повышение важности при этом
  //    делало цели **хуже**, чего человек не мог ни предвидеть, ни понять.
  const urgent = active.filter(
    (g) => (g.priority === 'critical' || g.priority === 'high') && g.kind !== 'emergency_fund',
  );
  for (const g of orderGoals(urgent)) {
    take(monthlyNeedFor(g, input.today), g, reasonForDeadline(g, input.today), 6);
  }

  // 7. Подушка до шести месяцев — это уже комфорт, и она может проиграть отпуску.
  if (buffer) {
    const need = sub(share(input.monthlyNeed, BUFFER_MONTHS.target), buffer.currentAmount);
    take(clampPos(need), buffer, 'Полная подушка — шесть месяцев расходов', 7);
  }

  // 8. Средний и низкий приоритет — пропорционально желаемым взносам.
  const restGoals = active.filter(
    (g) => (g.priority === 'medium' || g.priority === 'low') && goalGap(g) > ZERO,
  );
  if (restGoals.length > 0 && rest > ZERO) {
    const weights = restGoals.map((g) => Math.max(1, monthlyNeedFor(g, input.today)));
    const parts = distribute(rest, weights);
    restGoals.forEach((g, i) => {
      take(parts[i] ?? ZERO, g, 'Остаток распределён пропорционально желаемым взносам', 8);
    });
  }

  return { allocations: mergeByGoal(allocations), leftover: rest };
}

const goalGap = (g: Goal): Money => clampPos(sub(g.targetAmount, g.currentAmount));

/**
 * Почему этот долг гасим сейчас — в деньгах, а не в процентах.
 *
 * «Закроет 39 000 ₽ процентов за год» человек понимает сразу, «ставка 39%»
 * требует умножения в уме. У беспроцентного долга выгоды нет, и говорить
 * обратное было бы неправдой.
 */
function earlyRepayReason(loan: Loan | undefined, gap: Money): string {
  if (!loan) return 'Долг дороже любой цели по накоплению — гасим первым';
  const saved = savedPerYear(loan, gap);
  if (saved <= ZERO) {
    return annualCost(loan) <= ZERO
      ? 'Беспроцентный долг — досрочное гашение ничего не экономит'
      : 'Гасим по очереди';
  }
  return `Ставка ${loan.ratePct}% — это ${formatMoney(saved)} процентов за год`;
}

/**
 * С какого дня цель начинает копить.
 *
 * Дата начала в прошлом ничего не меняет: отсчёт всё равно идёт от сегодня,
 * потому что вчерашние месяцы уже не наступят.
 */
export const goalStart = (g: Goal, today: PlainDate): PlainDate =>
  g.startDate && g.startDate > today ? g.startDate : today;

/**
 * Сколько нужно откладывать в месяц, чтобы успеть к сроку.
 *
 * Месяцы считаются от даты начала, а не от сегодня: если копить начинаем в
 * ноябре, а срок декабрьский, то месяц остался один — и взнос должен вырасти,
 * а не размазаться по времени, которого нет. Приложение обязано назвать это
 * число, иначе отодвинутая цель выглядит бесплатной.
 */
export function monthlyNeedFor(g: Goal, today: PlainDate): Money {
  const gap = goalGap(g);
  if (gap <= ZERO) return ZERO;
  if (!g.targetDate) return g.monthlyContribution;
  const days = diffDays(goalStart(g, today), g.targetDate);
  if (days <= 0) return gap;
  const months = Math.max(1, Math.round(days / 30));
  return share(gap, 1 / months);
}

/**
 * Очередь целей: сначала важность, потом близость срока.
 *
 * Живёт в одном месте, потому что порядок показывается человеку трижды — в
 * распределении дохода, в главной задаче и в напоминании отложить. Три копии
 * этого правила разъехались бы, и приложение в один день назвало бы главной
 * одну цель, а денег попросило бы на другую.
 */
export function orderGoals(goals: readonly Goal[]): Goal[] {
  const rank: Record<Goal['priority'], number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return [...goals].sort((a, b) => rank[a.priority] - rank[b.priority] || byDate(a, b));
}

/** Ближе срок — раньше в очереди; без срока цель уходит в конец. */
const byDate = (a: Goal, b: Goal): number => {
  if (a.targetDate && b.targetDate) return a.targetDate < b.targetDate ? -1 : 1;
  if (a.targetDate) return -1;
  if (b.targetDate) return 1;
  return 0;
};

const byDeadline = (goals: Goal[], today: PlainDate): Goal[] =>
  [...goals].sort((a, b) => {
    if (a.targetDate && b.targetDate) return a.targetDate < b.targetDate ? -1 : 1;
    if (a.targetDate) return -1;
    if (b.targetDate) return 1;
    return 0;
  });

function reasonForDeadline(g: Goal, today: PlainDate): string {
  if (!g.targetDate) return 'Высокий приоритет';
  const months = Math.max(1, Math.round(diffDays(today, g.targetDate) / 30));
  return `Срок через ${months} мес — график не сдвигается`;
}

/** Одна цель могла получить деньги на нескольких шагах — складываем. */
function mergeByGoal(list: Allocation[]): Allocation[] {
  const byId = new Map<string, Allocation>();
  for (const a of list) {
    const prev = byId.get(a.goalId);
    if (!prev) byId.set(a.goalId, { ...a });
    else {
      prev.amount = add(prev.amount, a.amount);
      // объяснение оставляем от самого приоритетного шага
      if (a.step < prev.step) { prev.reason = a.reason; prev.step = a.step; }
    }
  }
  return [...byId.values()].sort((a, b) => a.step - b.step);
}

/** Прогноз: когда цель будет достигнута при текущем взносе. */
export function goalEta(g: Goal, monthlyContribution: Money, today: PlainDate): PlainDate | null {
  const gap = goalGap(g);
  if (gap <= ZERO) return today;
  if (monthlyContribution <= ZERO) return null;
  const months = Math.ceil(gap / monthlyContribution);
  // Дальше 50 лет — не прогноз: крошечный взнос к большой цели дал бы дату
  // за 9999 годом, и приложение упало бы на ней, как у долга (18.10.2026).
  if (months > 600) return null;
  return addMonths(today, months);
}

export interface BufferView {
  stage: BufferStage;
  months: number;
  label: string;
  nextThresholdAmount: Money;
}

/** Как подушка показывается пользователю: порог словами, а не только процент. */
export function bufferView(current: Money, monthlyNeed: Money): BufferView {
  const stage = bufferStage(current, monthlyNeed);
  const months = monthlyNeed > ZERO ? Math.round((current / monthlyNeed) * 10) / 10 : 0;
  const labels: Record<BufferStage, string> = {
    critical: 'критично',
    base: 'база',
    target: 'цель',
    done: 'собрана',
  };
  const nextMonths = stage === 'critical' ? 1 : stage === 'base' ? 3 : stage === 'target' ? 6 : 0;
  return {
    stage,
    months,
    label: labels[stage],
    nextThresholdAmount: nextMonths > 0 ? share(monthlyNeed, nextMonths) : current,
  };
}

/**
 * Сколько откладывать в месяц, чтобы успеть к сроку — до сотни рублей вверх.
 * Месяцев до срока не меньше одного: срок в этом месяце — весь остаток сразу.
 */
export function monthlyForDeadline(g: Goal, deadline: PlainDate, today: PlainDate): Money {
  const gap = goalGap(g);
  if (gap <= ZERO) return ZERO;
  const [y1, m1, d1] = today.split('-').map(Number) as [number, number, number];
  const [y2, m2, d2] = deadline.split('-').map(Number) as [number, number, number];
  const months = Math.max(1, (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0));
  return (Math.ceil(gap / months / 10_000) * 10_000) as Money;
}
